import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  depositsApi,
  cashWithdrawalsApi,
  personalWithdrawalsApi,
} from '@/services/accountingService';
import { bcadd, bctoFixed } from "@/hooks/useBcmath";

interface DepositsWithdrawalsState {
  deposits: any[];
  deposit: any | null;
  cashWithdrawals: any[];
  cashWithdrawal: any | null;
  personalWithdrawals: any[];
  personalWithdrawal: any | null;
  loading: boolean;
  // Count of in-flight thunks (M: shared `loading` used to flip false as
  // soon as any one concurrent request settled, flickering spinners).
  pending: number;
  error: any | null;
}

const initialState: DepositsWithdrawalsState = {
  deposits: [],
  deposit: null,
  cashWithdrawals: [],
  cashWithdrawal: null,
  personalWithdrawals: [],
  personalWithdrawal: null,
  loading: false,
  pending: 0,
  error: null,
};

// Paginated extractors can yield `{ data: [...] }` instead of a plain array;
// list state must always stay an array so unshift/filter/reduce never break.
const toList = (payload: any): any[] => (Array.isArray(payload) ? payload : (payload?.data ?? []));

// Deposits
export const fetchDeposits = createAsyncThunk('depositsWithdrawals/fetchDeposits', async (params: Record<string, any> | undefined, { rejectWithValue }) => {
  try {
    const response: any = await depositsApi.getAll(params || {});
    return response.data.data || response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const createDeposit = createAsyncThunk('depositsWithdrawals/createDeposit', async (data: any, { rejectWithValue }) => {
  try {
    const response: any = await depositsApi.create(data);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Cash Withdrawals
export const fetchCashWithdrawals = createAsyncThunk('depositsWithdrawals/fetchCashWithdrawals', async (params: Record<string, any> | undefined, { rejectWithValue }) => {
  try {
    const response: any = await cashWithdrawalsApi.getAll(params || {});
    return response.data.data || response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const createCashWithdrawal = createAsyncThunk('depositsWithdrawals/createCashWithdrawal', async (data: any, { rejectWithValue }) => {
  try {
    const response: any = await cashWithdrawalsApi.create(data);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Personal Withdrawals
export const fetchPersonalWithdrawals = createAsyncThunk('depositsWithdrawals/fetchPersonalWithdrawals', async (params: Record<string, any> | undefined, { rejectWithValue }) => {
  try {
    const response: any = await personalWithdrawalsApi.getAll(params || {});
    return response.data.data || response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const fetchPersonalWithdrawal = createAsyncThunk('depositsWithdrawals/fetchPersonalWithdrawal', async (id: number | string, { rejectWithValue }) => {
  try {
    const response: any = await personalWithdrawalsApi.getById(id);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const createPersonalWithdrawal = createAsyncThunk('depositsWithdrawals/createPersonalWithdrawal', async (data: any, { rejectWithValue }) => {
  try {
    const response: any = await personalWithdrawalsApi.create(data);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const updatePersonalWithdrawal = createAsyncThunk('depositsWithdrawals/updatePersonalWithdrawal', async ({ id, data }: { id: number | string; data: any }, { rejectWithValue }) => {
  try {
    const response: any = await personalWithdrawalsApi.update(id, data);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

export const deletePersonalWithdrawal = createAsyncThunk('depositsWithdrawals/deletePersonalWithdrawal', async (id: number | string, { rejectWithValue }) => {
  try {
    await personalWithdrawalsApi.delete(id);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

const depositsWithdrawalsSlice = createSlice({
  name: 'depositsWithdrawals',
  initialState,
  reducers: {
    clearPersonalWithdrawal(state) {
      state.personalWithdrawal = null;
    },
    resetDepositsWithdrawals(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    // Count-based pending tracking: deposits, cash withdrawals and personal
    // withdrawals run concurrently, so a single request settling must not
    // clear `loading` while other requests are still in flight.
    const handlePending = (state: DepositsWithdrawalsState) => {
      state.pending += 1;
      state.loading = true;
      state.error = null;
    };
    const handleSettled = (state: DepositsWithdrawalsState) => {
      state.pending = Math.max(0, state.pending - 1);
      state.loading = state.pending > 0;
    };
    const handleRejected = (state: DepositsWithdrawalsState, action: any) => {
      handleSettled(state);
      state.error = action.payload;
    };

    builder
      .addCase(fetchDeposits.pending, handlePending)
      .addCase(fetchDeposits.fulfilled, (state, action) => {
        handleSettled(state);
        state.deposits = toList(action.payload);
      })
      .addCase(fetchDeposits.rejected, handleRejected)
      
      .addCase(createDeposit.pending, handlePending)
      .addCase(createDeposit.fulfilled, (state, action) => {
        handleSettled(state);
        state.deposits.unshift(action.payload);
      })
      .addCase(createDeposit.rejected, handleRejected)

      .addCase(fetchCashWithdrawals.pending, handlePending)
      .addCase(fetchCashWithdrawals.fulfilled, (state, action) => {
        handleSettled(state);
        state.cashWithdrawals = toList(action.payload);
      })
      .addCase(fetchCashWithdrawals.rejected, handleRejected)

      .addCase(createCashWithdrawal.pending, handlePending)
      .addCase(createCashWithdrawal.fulfilled, (state, action) => {
        handleSettled(state);
        state.cashWithdrawals.unshift(action.payload);
      })
      .addCase(createCashWithdrawal.rejected, handleRejected)

      .addCase(fetchPersonalWithdrawals.pending, handlePending)
      .addCase(fetchPersonalWithdrawals.fulfilled, (state, action) => {
        handleSettled(state);
        state.personalWithdrawals = toList(action.payload);
      })
      .addCase(fetchPersonalWithdrawals.rejected, handleRejected)

      .addCase(fetchPersonalWithdrawal.pending, handlePending)
      .addCase(fetchPersonalWithdrawal.fulfilled, (state, action) => {
        handleSettled(state);
        state.personalWithdrawal = action.payload;
      })
      .addCase(fetchPersonalWithdrawal.rejected, handleRejected)

      .addCase(createPersonalWithdrawal.pending, handlePending)
      .addCase(createPersonalWithdrawal.fulfilled, (state, action) => {
        handleSettled(state);
        state.personalWithdrawals.unshift(action.payload);
      })
      .addCase(createPersonalWithdrawal.rejected, handleRejected)

      .addCase(updatePersonalWithdrawal.pending, handlePending)
      .addCase(updatePersonalWithdrawal.fulfilled, (state, action) => {
        handleSettled(state);
        // String-normalized id match: backend may serialize ids as strings
        // while the cached list holds numbers (strict === silently no-ops).
        const index = state.personalWithdrawals.findIndex(
          (p: any) => String(p.id) === String(action.payload.id)
        );
        if (index !== -1) {
          state.personalWithdrawals[index] = action.payload;
        }
      })
      .addCase(updatePersonalWithdrawal.rejected, handleRejected)

      .addCase(deletePersonalWithdrawal.pending, handlePending)
      .addCase(deletePersonalWithdrawal.fulfilled, (state, action) => {
        handleSettled(state);
        state.personalWithdrawals = state.personalWithdrawals.filter(
          (p: any) => String(p.id) !== String(action.payload)
        );
      })
      .addCase(deletePersonalWithdrawal.rejected, handleRejected);
  },
});

export const { clearPersonalWithdrawal, resetDepositsWithdrawals } = depositsWithdrawalsSlice.actions;

export const selectDeposits = (state: any) => state.depositsWithdrawals.deposits;
// M-17 — BC-math accumulation; the float reduce drifted on 4-dp sums.
// Non-numeric amounts ('abc', '1,000', '1.2.3') crash BigInt parsing in
// bcadd/bctoFixed, so skip any row whose amount is not a clean decimal.
export const selectTotalDeposits = (state: any) =>
  state.depositsWithdrawals.deposits.reduce((sum: string, d: any) => {
    const amount = String(d?.amount ?? '').trim();
    if (amount !== '' && !/^-?\d+(\.\d+)?$/.test(amount)) {
      return sum;
    }
    return bcadd(sum, bctoFixed(amount || 0));
  }, '0');
export const selectCashWithdrawals = (state: any) => state.depositsWithdrawals.cashWithdrawals;
export const selectPersonalWithdrawals = (state: any) => state.depositsWithdrawals.personalWithdrawals;
export const selectPersonalWithdrawal = (state: any) => state.depositsWithdrawals.personalWithdrawal;
export const selectDepositsWithdrawalsLoading = (state: any) => state.depositsWithdrawals.loading;
export const selectDepositsWithdrawalsError = (state: any) => state.depositsWithdrawals.error;

export default depositsWithdrawalsSlice.reducer;
